/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import Backbone from 'backbone';
import { View } from 'backbone.marionette';

import OrderTypes from '@/enums/OrderTypes';

import loadOrderPackageDeliveryScheduleGridView from '../../loaders/OrderPackageDeliveryScheduleGridViewLoader';
import loadOrderPackageRelocationDeliveryScheduleGridView from '../../loaders/OrderPackageRelocationDeliveryScheduleGridViewLoader';
import loadOrderPackageSelectOrdersView from '../../loaders/OrderPackageSelectOrdersViewLoader';
import loadOrderPackageInformationView from '../../loaders/OrderPackageInformationFormLoader';
import DeliverySchedulePrintView from '../../../common/DeliverySchedulePrintView/DeliverySchedulePrintView';
import OrdersPrintCollectionLoader from '../../../order/loaders/OrdersPrintCollectionLoader';
import OrdersPrintCollectionView from '../../../order/views/OrdersPrintCollectionView';
import GetPackagedCustomerOrdersByOrderPackageID from '../../services/GetPackagedCustomerOrdersByOrderPackageID';
import './OrderPackagePrintPage.css';

import tmplOrderPackagePrintPage from './OrderPackagePrintPage.hbs';

const OrderPackagePrintPage = View.extend({
  template: tmplOrderPackagePrintPage,

  className: 'order-package-print-page container-fluid',

  ui: {
    selectedOrdersView: '.selected-orders-print-view',
    deliveryScheduleView: '.delivery-schedule-print-view',
    informationView: '.information-print-view',
    printPage: '.print-page'
  },

  regions: {
    selectedOrdersView: '@ui.selectedOrdersView',
    deliveryScheduleView: '@ui.deliveryScheduleView',
    informationView: '@ui.informationView',
    printPage: '@ui.printPage'
  },

  templateContext() {
    const orderPackage = this.model.get('orderPackage');
    return {
      isRelocation: orderPackage.orderFormTypeID === OrderTypes.RELOCATION
    };
  },

  async onBeforeRender() {
    const orderPackage = this.model.get('orderPackage');
    const { orderPackageID } = this.options;
    const isEditable = false;
    const isRelocation = orderPackage.orderFormTypeID === OrderTypes.RELOCATION;
    const deliveryLoader = isRelocation
      ? loadOrderPackageRelocationDeliveryScheduleGridView
      : loadOrderPackageDeliveryScheduleGridView;

    const [selectedOrdersView, deliveryScheduleGridView, packageInfoView] = await Promise.all([
      loadOrderPackageSelectOrdersView({ orderPackageID, editable: isEditable }),
      deliveryLoader({ orderPackageID, isEditable, orderPackage }),
      loadOrderPackageInformationView({ orderPackageID, editable: isEditable })
    ]);

    let deliverySchedules = [];

    if (orderPackage.orderFormTypeID === OrderTypes.RELOCATION) {
      const orders = await GetPackagedCustomerOrdersByOrderPackageID(orderPackageID);
      this.packagedOrdersCollection = new Backbone.Collection(orders);
      const collectionView = await OrdersPrintCollectionLoader(this.packagedOrdersCollection);
      const ordersPrintCollectionView = new OrdersPrintCollectionView({
        collection: collectionView,
        itemsRendered: 0
      });
      this.showChildView('printPage', ordersPrintCollectionView);
    } else {
      deliverySchedules = deliveryScheduleGridView.model.attributes.schedules;

      this.showChildView('selectedOrdersView', selectedOrdersView);
      this.showChildView(
        'deliveryScheduleView',
        new DeliverySchedulePrintView({
          model: new Backbone.Model({
            deliverySchedules,
            isRelocation: orderPackage.orderFormTypeID === OrderTypes.RELOCATION ? true : false,
            isSpecialtyRequirement: orderPackage.orderFormTypeID === OrderTypes.SPECIALTY_REQ ? true : false
          })
        })
      );
      this.showChildView('informationView', packageInfoView);
      selectedOrdersView.packagedOrdersGrid.collection.setPageSize(1000);
      window.print();
    }
  }
});

export default OrderPackagePrintPage;
